import Layout from '../components/Layout';
import React, {useEffect, useState} from 'react';
import {useForm, Controller} from 'react-hook-form';
import {DateTime} from 'luxon';
import {toast} from 'react-toastify';
import {FormItem, Tooltip, Input, Select, Button} from '@mayo/mayo-ui-gakki';
import {StepStatus, useAppContext} from '../context/app-context';
import SubHeader from '../components/SubHeader';
import {useAuthContext, useClient} from 'app/gakki/context/auth-context';
import {useCompanyContext} from '../context/company-context';
import {useSaveMutate} from '../api/system-admin';
import cx from 'classnames';
import {sortByEmployeeJobNo} from 'utils';
import {useMutation, useQueryClient} from 'react-query';
import {ReactComponent as InfoIcon} from '@common/assets/icon_info-circle.svg';

const Row = ({children, className, ...props}) => {
  return (
    <div {...props} className={cx('grid pt-4 gap-4', className)}>
      {children}
    </div>
  );
};

const Container = ({children, className, ...props}) => {
  return (
    <div {...props} className={cx('h-full', className)}>
      {children}
    </div>
  );
};
const Form = ({children, className, ...props}) => {
  return (
    <div
      {...props}
      className={cx(
        'm-auto w-[441px] h-[355px] bg-white rounded border px-4 pb-[123.5px] border-[#e3e4e6]',
        className,
      )}
    >
      {children}
    </div>
  );
};

export default function StepAdmin() {
  const api = useClient();
  const queryClient = useQueryClient();
  const {companyId} = useAuthContext();
  const {
    data,
    changeStep,
    updatedTime,
    stepStatus,
    init,
    edit,
    save,
    setIsLoading,
    isLoading,
    step,
    saveAndGoStep,
    saveFail,
    confirm,
    refetchEnablePY,
    onPrev,
  } = useAppContext();
  const {employee} = useCompanyContext();
  const [shouldUpdateLocal, setShouldUpdateLocal] = useState(false);
  const {
    control,
    formState,
    errors,
    watch,
    handleSubmit,
    reset,
    register,
    setValue,
  } = useForm({
    defaultValue: {
      staffId: '',
      email: '',
      name: '',
    },
  });

  useEffect(() => {
    setIsLoading(true);
    if (data !== null && stepStatus !== StepStatus.init) {
      setIsLoading(false);
      reset(data);
      return;
    }
    api
      .get(`user/company/${companyId}/xeuser`)
      .then(({data}) => {
        init({staffId: data?.id, email: data?.email, name: data?.name});
        reset({staffId: data?.id, email: data?.email, name: data?.name});
        return;
      })
      .finally(() => setIsLoading(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveData = data => {
    return api.put(`user/company/${companyId}/xeuser`, {
      data,
    });
  };

  const confirmData = () => {
    return api.put(`user/company/${companyId}/xeuser/confirm`);
  };

  const {mutateAsync: saveToServer} = useSaveMutate({
    onSuccess: () => save(),
    onError: () => saveFail(),
    onMutate: () => setIsLoading(true),
    onSettled: () => setIsLoading(false),
  });

  const {mutateAsync: handleConfirm} = useMutation(
    data =>
      saveData(data)
        .then(() => refetchEnablePY('next', step + 1))
        .then(confirmData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employee-list');
        confirm();
        changeStep(step + 1);
      },
      onMutate: () => setIsLoading(true),
      onSettled: () => setIsLoading(false),
    },
  );

  useEffect(() => {
    if (shouldUpdateLocal) {
      updateLocalData();
      setShouldUpdateLocal(false);
    }
  }, [shouldUpdateLocal]);

  useEffect(() => {
    if (typeof saveAndGoStep === 'number') {
      handleSave();
    }
  }, [saveAndGoStep]);

  const handleNext = async data => {
    const staff = employee.find(e => e.id === data.staffId);
    const onboardingDate = staff.onBoardingDate;
    if (DateTime.fromFormat(onboardingDate, 'yyyy/MM/dd') > DateTime.now()) {
      toast.error('管理員需為已到職人員，請至 [人員] 調整到職日。', {
        hideProgressBar: true,
        position: 'top-center',
        autoClose: 2000,
      });
      return;
    }
    await handleConfirm(data);
  };

  const handleSave = async () => {
    const formValues = watch();
    try {
      await saveToServer({...formValues, email: formValues?.email || null});
    } catch (ex) {
      console.error(ex);
    }
  };

  const updateLocalData = () => {
    const formValues = watch();
    edit(formValues);
  };

  return (
    <React.Fragment>
      <Container>
        <Form>
          <SubHeader className={'!h-[66.3px] !m-0'}>
            請協助指派系統管理員
          </SubHeader>
          <Row>
            <Input hidden name="name" ref={register} />
            <FormItem label="請選擇員工">
              <Controller
                render={({onChange, value}) => (
                  <Select
                    value={employee.find(e => e.id === value)}
                    onChange={o => {
                      if (o) {
                        onChange(o.id);
                        setValue('name', o.name);
                        setValue('email', o.businessEmail || '');
                      } else {
                        onChange(null);
                        setValue('name', null);
                      }
                      setShouldUpdateLocal(true);
                    }}
                    isError={formState.isSubmitted && errors.staffId}
                    placeholder="請選擇員工"
                    getOptionLabel={o => (
                      <span
                        data-test-id={`option-${o.employeeNumber}`}
                      >{`${o.employeeNumber}/${o.name}`}</span>
                    )}
                    getOptionValue={o => o.id}
                    options={employee.sort(sortByEmployeeJobNo)}
                  />
                )}
                name="staffId"
                control={control}
                rules={{required: true}}
              />
            </FormItem>
            <FormItem
              label={
                <div className="flex items-center gap-[5px]">
                  員工公司信箱
                  <Tooltip
                    content="連動 「人員」 頁面的公司信箱"
                    placement="bottom-start"
                    isIcon={true}
                  >
                    <Button
                      variant="simple"
                      colorSchema="primary"
                      icon={<InfoIcon />}
                    />
                  </Tooltip>
                </div>
              }
              errorMessage={errors.email?.message}
            >
              <Controller
                render={({onChange, value}) => (
                  <Input
                    data-test-id={'email'}
                    value={value}
                    onChange={e => {
                      onChange(e);
                      setShouldUpdateLocal(true);
                    }}
                    isError={formState.isSubmitted && errors.email}
                    placeholder="請輸入員工信箱"
                  />
                )}
                name="email"
                control={control}
                rules={{
                  required: true,
                  validate: {
                    emailDuplicate: v => {
                      const formValues = watch();
                      const isExist = employee.some(
                        e =>
                          e.businessEmail === v && e.id !== formValues?.staffId,
                      );
                      return isExist ? '信箱重覆' : true;
                    },
                    emailValidate: v => {
                      const emailRegex =
                        /^\w+((-\w+)|(\.\w+))*@[A-Za-z0-9]+((\.|-)[A-Za-z0-9]+)*\.[A-Za-z]+$/;
                      return emailRegex.test(v) || '信箱格式錯誤';
                    },
                  },
                }}
              />
            </FormItem>
          </Row>
        </Form>
      </Container>
      <Layout.Footer
        showNextButton
        showPrevButton
        showSaveButton
        onPrev={onPrev}
        onNext={handleSubmit(data => handleNext(data))}
        onSave={handleSave}
        updatedTime={updatedTime}
        disabledSaveButton={stepStatus !== StepStatus.edit || isLoading}
        disabledPrevButton={stepStatus === StepStatus.edit || isLoading}
        disabledNextButton={isLoading}
      />
    </React.Fragment>
  );
}
